import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken, getOrganizationId } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";


export const createExpense = createAsyncThunk(
  "expense/createExpense",
  async (expenseData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const organizationId = getOrganizationId();

      // The data is already properly structured from the form
      const requestData = {
        ...expenseData,
        organizationID: organizationId, // Add organizationID from cookie
      };


      const response = await secureAxios.post(
        `${BASE_URL}/v1/expense`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchExpenses = createAsyncThunk(
  "expense/fetchExpenses",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(`${BASE_URL}/v1/expense`, {
        params: {
          skip,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return {
        expenses: response.data.data,
        total: response.data.total || response.data.data.length,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchExpenseByAny = createAsyncThunk(
  "expense/fetchExpenseByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/expense/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data.data,
        total: response.data.data.length,
        isSingleExpense: key === "_id",
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleExpense: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateExpense = createAsyncThunk(
  "expense/updateExpense",
  async (expenseData, { rejectWithValue }) => {

    try {
    const token = getAccessToken();
      const organizationId = getOrganizationId();

      const requestData = {
        ...expenseData,
        organizationID: organizationId, // Add organizationID from cookie
      };
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/expense`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteExpense = createAsyncThunk(
  "expense/deleteExpense",
  async (expenseId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(`${BASE_URL}/v1/expense`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [expenseId],
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchExpenseTotals = createAsyncThunk(
  "expense/fetchExpenseTotals",
  async (organizationID, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const orgId = organizationID || getOrganizationId();
      if (!orgId) throw new Error("No organization ID found");

      const response = await secureAxios.get(
        `${BASE_URL}/v1/expense/total-expenses/${orgId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: true,
      requiresDecryption: true,
    },
  }
);

const initialState = {
  expenses: [],
  fetchedExpenses: [],
  currentExpense: null,
  total: 0,
  expenseTotals: null,
  isLoadingTotals: false,
  totalsError: null,
  expenseDetails: {
    transactionName: "",
    description: "",
    amount: "",
    debitAccount: "",
    creditAccount: "",
  },
  isLoading: false,
  error: null,
  success: false,
};

const expenseSlice = createSlice({
  name: "expense",
  initialState,
  reducers: {
    updateExpenseDetails: (state, action) => {
      state.expenseDetails = {
        ...state.expenseDetails,
        ...action.payload,
      };
    },
    resetExpenseState: () => initialState,
    loadCachedData: (state, action) => {
      return {
        ...state,
        expenses: action.payload.expenses || state.expenses,
        fetchedExpenses:
          action.payload.fetchedExpenses || state.fetchedExpenses,
        currentExpense: action.payload.currentExpense || state.currentExpense,
        total: action.payload.total || state.total,
        expenseDetails: action.payload.expenseDetails || state.expenseDetails,
        isLoading: false,
        error: null,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createExpense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createExpense.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchExpenses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenses = action.payload.expenses;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchExpenseByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenseByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedExpenses = action.payload.data;

        if (action.payload.isSingleExpense && action.payload.data.length > 0) {
          state.currentExpense = action.payload.data[0];
        } else if (action.payload.data.length > 0) {
          state.currentExpense = action.payload.data[0];
        }

        if (!action.payload.isSingleExpense) {
          state.total = action.payload.total;
        }

        state.error = null;
      })
      .addCase(fetchExpenseByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateExpense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteExpense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchExpenseTotals.pending, (state) => {
        state.isLoadingTotals = true;
        state.totalsError = null;
      })
      .addCase(fetchExpenseTotals.fulfilled, (state, action) => {
        state.isLoadingTotals = false;
        state.expenseTotals = action.payload;
        state.totalsError = null;
      })
      .addCase(fetchExpenseTotals.rejected, (state, action) => {
        state.isLoadingTotals = false;
        state.totalsError = action.payload;
      });
  },
});

// Export actions
export const { updateExpenseDetails, resetExpenseState, loadCachedData } =
  expenseSlice.actions;

// Export selectors
export const selectExpenseDetails = (state) => state.expense.expenseDetails;
export const selectExpenseLoading = (state) => state.expense.isLoading;
export const selectExpenseError = (state) => state.expense.error;
export const selectExpenseSuccess = (state) => state.expense.success;
export const selectAllExpenses = (state) => state.expense.expenses;
export const selectFetchedExpenses = (state) =>
  state.expense.fetchedExpenses || [];
export const selectCurrentExpense = (state) => state.expense.currentExpense;
export const selectExpenseTotal = (state) => state.expense.total;
export const selectExpenseTotals = (state) => state.expense.expenseTotals;
export const selectIsLoadingExpenseTotals = (state) =>
  state.expense.isLoadingTotals;
export const selectExpenseTotalsError = (state) => state.expense.totalsError;

export const selectIsOfflineData = (state) =>
  state.expense.isOfflineData || false;
export default expenseSlice.reducer;
